import useEditorProps from '@hooks/useEditorProps';
import {
    AddPhotoAlternateRounded,
    DeleteOutlineRounded,
    Upload,
} from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Typography,
    alpha,
} from '@mui/material';
import { AxiosProgressEvent } from 'axios';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

function FileField({
    value,
    onChange,
    multiple = false,
    accept = 'image/*',
    dropText,
    sx = {},
    backgroundImage = false,
    Icon = AddPhotoAlternateRounded,
}) {
    const { t } = useTranslation();
    const [dragOver, setDragOver] = useState(false);
    const [progressEvent, setProgressEvent] =
        useState<AxiosProgressEvent | null>(null);
    const [uploading, setUploading] = useState(false);
    const { uploadFile } = useEditorProps();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const isSingleImage = !multiple && accept === 'image/*';

    const parsedValue = backgroundImage
        ? value.replace(/url\((.*?)\)/, '$1')
        : value;

    const handleDragEnter = (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(true);
    };

    const handleDragLeave = (event: DragEvent) => {
        if (
            event.relatedTarget instanceof Element &&
            event.currentTarget.contains(event.relatedTarget)
        ) {
            return;
        }
        setDragOver(false);
    };

    const handleUpload = async (file: File) => {
        setUploading(true);
        await uploadFile(file, setProgressEvent).then(url => {
            const parsedUrl = backgroundImage ? `url(${url})` : url;
            onChange(parsedUrl);
        });
        setProgressEvent(null);
        setUploading(false);
    };

    const handleDrop = (event: DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setDragOver(false);
        if (event.dataTransfer.files?.[0]?.type?.startsWith('image/')) {
            handleUpload(event.dataTransfer.files?.[0]);
        }
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleUpload(event.target.files?.[0]);
    };

    return (
        <Box
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragOver={e => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onClick={() => fileInputRef.current?.click()}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                height: '100%',
                cursor: 'pointer',
                backgroundColor: theme => theme.palette.action.hover,
                ...(dragOver && {
                    backgroundColor: theme =>
                        alpha(
                            theme.palette.primary.main,
                            theme.palette.action.activatedOpacity,
                        ),
                }),
                borderColor: theme => theme.palette.primary.main,
                borderStyle: 'dashed',
                borderWidth: 3,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                mb: 2,
                p: 1,
                '& > #button': {
                    display: 'none',
                },
                '&:hover > #button': {
                    display: 'block',
                },
                ...sx,
            }}
        >
            {parsedValue && isSingleImage ? (
                <>
                    <img
                        src={parsedValue}
                        style={{
                            objectFit: 'scale-down',
                        }}
                    />
                    <IconButton
                        id="button"
                        color="error"
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            backgroundColor: 'white',
                            opacity: 0.8,
                            height: 32,
                            width: 32,
                            // px: 2,
                            // py: 0.5,
                            '&:hover': {
                                backgroundColor: 'white',
                                opacity: 1,
                            },
                            boxShadow: theme => theme.shadows[1],
                        }}
                        onClick={e => {
                            e.stopPropagation();
                            onChange('');
                        }}
                    >
                        <DeleteOutlineRounded />
                    </IconButton>
                    <Button
                        id="button"
                        sx={{
                            position: 'absolute',
                            right: '50%',
                            backgroundColor: 'white',
                            transform: 'translateX(50%)',
                            opacity: 0.8,
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                                backgroundColor: 'white',
                                opacity: 1,
                            },
                            boxShadow: theme => theme.shadows[1],
                        }}
                    >
                        {t('Replace')}
                    </Button>
                </>
            ) : (
                <Box>
                    {!uploading ? (
                        <>
                            <Typography color="text.secondary" align="center">
                                {dropText ||
                                    t('Drop or click to upload an image')}
                            </Typography>
                            <Icon
                                sx={{
                                    fontSize: 42,
                                    color: 'text.secondary',
                                    display: 'block',
                                    mt: 1,
                                    marginX: 'auto',
                                }}
                            />
                        </>
                    ) : (
                        <>
                            <Typography color="text.secondary" align="center">
                                {dropText || t('Uploading')}...
                            </Typography>
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 1,
                                }}
                            >
                                <CircularProgress
                                    variant={
                                        !progressEvent
                                            ? 'indeterminate'
                                            : 'determinate'
                                    }
                                    value={progressEvent?.progress * 100}
                                />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {progressEvent && (
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            sx={{ color: 'text.secondary' }}
                                        >{`${Math.round(progressEvent?.progress * 100)}%`}</Typography>
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            )}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleInputChange}
                multiple={multiple}
                accept={accept}
            />
        </Box>
    );
}

export default FileField;
